import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { appRoute, useAppLayoutContext } from "./route";
// Dashboard is the landing route — needed on virtually every cold entry, so it
// ships in the main bundle (NOT lazy) and renders directly with the shell.
// No <Suspense> skeleton wraps it: it's local-first (SQLite reads resolve in
// ~50ms) and hydrates synchronously from the TanStack cache, so a skeleton
// would only flash + cause a remount. All its queries are non-suspending.
import { Dashboard } from "@/features/dashboard/components/dashboard";

interface DashboardSearch {
  /** Search for lists/folders. Makes the filtered view copyable/shareable. */
  q?: string;
  /** Collapsed group ids (CSV). Makes the dashboard state copyable. */
  collapsed?: string;
}

function DashboardPage() {
  const { openChoiceDialog } = useAppLayoutContext();
  const search = useSearch({ from: "/_app/" });
  const navigate = useNavigate();

  const collapsedGroups = () => {
    const raw = search().collapsed;
    return raw ? raw.split(",").filter(Boolean) : [];
  };

  return (
    <Dashboard
      onAddList={(groupId) => {
        openChoiceDialog(groupId);
      }}
      searchQuery={search().q}
      onSearchQueryChange={(q) => {
        // Idempotent: reloading the URL reconstructs exactly this state.
        // `replace` avoids inflating history while typing. We preserve the
        // rest of search (e.g. `collapsed`) with the reducer.
        void navigate({
          to: "/",
          search: (prev) => ({ ...prev, q: q ? q : undefined }),
          replace: true,
        });
      }}
      collapsedGroups={collapsedGroups()}
      onCollapsedGroupsChange={(ids) => {
        void navigate({
          to: "/",
          search: (prev) => ({ ...prev, collapsed: ids.length > 0 ? ids.join(",") : undefined }),
          replace: true,
        });
      }}
    />
  );
}

export const indexRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  validateSearch: (search: Record<string, unknown>): DashboardSearch => ({
    q: typeof search.q === "string" && search.q.trim() !== "" ? search.q : undefined,
    collapsed:
      typeof search.collapsed === "string" && search.collapsed.trim() !== ""
        ? search.collapsed
        : undefined,
  }),
  component: DashboardPage,
});
